import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import {
  AMENITY_CATEGORY_LABELS,
  AmenityCategory,
} from "@postnatal-care/shared-types";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getAmenityIcon } from "@/components/AmenityPicker/iconMap";
import type { AmenityMasterEntity } from "@/hooks/useBusinesses";

interface SortableAmenityRowProps {
  amenity: AmenityMasterEntity;
  onEdit: (a: AmenityMasterEntity) => void;
  onDelete: (a: AmenityMasterEntity) => void;
  onToggleActive: (a: AmenityMasterEntity, next: boolean) => void;
  toggling: boolean;
}

export const SortableAmenityRow = ({
  amenity,
  onEdit,
  onDelete,
  onToggleActive,
  toggling,
}: SortableAmenityRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: amenity.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const Icon = getAmenityIcon(amenity.iconKey);
  const catLabel = amenity.category
    ? AMENITY_CATEGORY_LABELS[amenity.category]?.en
    : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 bg-card ${
        !amenity.isActive ? "opacity-60" : ""
      }`}
    >
      <button
        type="button"
        className="p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{amenity.name}</span>
          {catLabel && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-primary/10 text-primary shrink-0">
              {catLabel}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {amenity.nameMalayalam || "—"} ·{" "}
          <span className="font-mono">{amenity.iconKey}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={amenity.isActive}
          onCheckedChange={(v) => onToggleActive(amenity, v)}
          disabled={toggling}
          aria-label={amenity.isActive ? "Deactivate" : "Activate"}
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => onEdit(amenity)}
          aria-label="Edit amenity"
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => onDelete(amenity)}
          aria-label="Delete amenity"
        >
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
};

export default SortableAmenityRow;
